import { makeAutoObservable, runInAction } from "mobx";
import { NotificationApi } from "../services/NotificationApi";
import {
  Notification,
  NotificationCounter,
  UserNotificationListItem,
} from "../models/Notification";
import { RootStore } from "../../shared/stores/RootStore";

export class NotificationStore {
  private rootStore;
  counter?: NotificationCounter;
  notifications: Notification[] = [];
  settings: UserNotificationListItem[] = [];
  private notificationsNextPage: number | null = 0;
  private notificationPageSize: number = 5;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  async getUserNotificationsSettings() {
    const { data } = await NotificationApi.getUserNotificationListItem();
    runInAction(() => {
      this.settings = data;
    });
  }

  updateUserNotificationListItem(
    groupKey: string,
    channelType: string,
    enabled: boolean
  ) {
    const updatedSettings = this.settings
      .find((notification) => notification.groupKey === groupKey)
      ?.channels?.find((channel) => channel.type === channelType);
    runInAction(() => {
      if (updatedSettings) updatedSettings.enabled = enabled;
    });
  }

  async updateNotificationSettings() {
    await NotificationApi.updateSettings({
      updatedSettings: this.settings,
    });
  }

  async fetchCounter(onCounterIncrease?: (amount: number) => void) {
    const { data } = await NotificationApi.getCounter();

    if (this.counter && data.total > this.counter.total) {
      onCounterIncrease?.(data.total - this.counter.total);
    }

    runInAction(() => {
      this.counter = data;
    });
  }

  resetNotifications() {
    this.notificationsNextPage = 0;
    this.notifications = [];
  }

  async fetchNotifications() {
    if (this.notificationsNextPage !== null) {
      const { data } = await NotificationApi.list(
        this.notificationPageSize,
        this.notificationsNextPage
      );
      runInAction(() => {
        this.notificationsNextPage = data.meta.nextPage;
        this.notifications.push(...data.data);
      });
    }
  }

  async readAll() {
    await NotificationApi.readAll();
    runInAction(() => {
      if (this.counter) {
        this.counter = { ...this.counter, unread: 0 };
      }
      this.notifications =
        this.notifications.map((n) => ({ ...n, read: true })) || [];
    });
  }

  async readNotificationById(notificationId: number) {
    const { data } = await NotificationApi.read(notificationId);
    runInAction(() => {
      this.counter = { ...this.counter!, unread: this.counter!.unread - 1 };
      this.notifications = this.notifications.map((n) =>
        n.id === notificationId ? data : n
      );
    });
  }

  async unreadNotificationById(notificationId: number) {
    const { data } = await NotificationApi.unread(notificationId);
    runInAction(() => {
      this.counter = { ...this.counter!, unread: this.counter!.unread + 1 };
      this.notifications = this.notifications.map((n) =>
        n.id === notificationId ? data : n
      );
    });
  }

  async archiveNotificationById(notificationId: number) {
    await NotificationApi.delete(notificationId);
    runInAction(() => {
      this.counter = {
        total: this.counter!.total - 1,
        unread: this.counter!.unread - 1,
      };
      this.notifications = this.notifications.filter(
        (n) => n.id !== notificationId
      );
    });
  }
}

